'use es6';

import { createSelector } from 'reselect';
import SeamlessImmutable from 'seamless-immutable';
import { mapObject, pick, reduce, some } from 'underscore';
import { byName as TemplateTypes } from 'ContentUtils/constants/TemplateTypes';
import { PUBLISHED_PAGE, DRAFT_PAGE, SENT_EMAIL, DRAFT_EMAIL, BLOG, BLOG_POST_TEMPLATE, BLOG_LISTING_TEMPLATE, CODED_FILE, CUSTOM_WIDGET, EMAIL_TEMPLATE, PAGE_TEMPLATE, SYSTEM_TEMPLATE, QUOTE_TEMPLATE, GLOBAL_GROUP, CONTENT_SETTINGS, GLOBAL_CONTENT_PARTIAL, SALES_QUOTE_TEMPLATE } from 'DesignData/constants/CosIndexConnectionCategories';
import CosIndexLiveConnectionCategories from 'DesignData/constants/CosIndexLiveConnectionCategories';
import * as RequestStatus from 'DesignData/enums/RequestStatus';
import { systemMembershipTemplateTypes } from 'DesignData/enums/SystemPageTemplateTypes';

const getConnectionsByAssetId = (state, props) => state.cosIndexConnections.byId[props.assetId];

const getFolderConnectionsAssetIds = (state, props) => state.cosIndexConnections.byFolderId[props.folderId];

const getFetchStatusByAssetId = (state, props) => state.cosIndexConnections.fetchStatusById[props.assetId];

const getFetchErrorByAssetId = (state, props) => state.cosIndexConnections.fetchErrorById[props.assetId];
/* eslint-disable eqeqeq */


function getConnectionsForTemplateTypes(parentConnections, templateTypes) {
  return reduce(parentConnections, (_result, _node) => {
    if ((_node.isLayout || _node.isCodedFile) &&
    /** @see {@link https://issues.hubspotcentral.com/browse/CG-15430?focusedCommentId=1222634#comment-1222634} */
    // /\.html$/.test(_node.model.path) &&
    some(templateTypes, templateTypeId => templateTypeId == _node.model.templateType)) {
      _result[_node.model.id] = _node;
    }

    return _result;
  }, {});
}

function getConnections(parentConnections, predicate) {
  return reduce(parentConnections, (_result, _node) => {
    if (predicate(_node)) {
      _result[_node.model.id] = _node;
    }

    return _result;
  }, {});
}
/* eslint-enable eqeqeq */


const makeGetParentConnections = () => createSelector([getConnectionsByAssetId], (connections = {}) => connections.parentConnections || {});

const makeGetPageTemplateConnections = () => {
  const getParentConnections = makeGetParentConnections();
  return createSelector([getParentConnections], parentConnections => {
    return getConnectionsForTemplateTypes(parentConnections, [TemplateTypes.landing_page_base_template.id, TemplateTypes.landing_page.id, TemplateTypes.site_page.id]);
  });
};

const makeGetBlogPostTemplateConnections = () => {
  const getParentConnections = makeGetParentConnections();
  return createSelector([getParentConnections], parentConnections => {
    return getConnectionsForTemplateTypes(parentConnections, [TemplateTypes.blog.id, TemplateTypes.blog_post.id]);
  });
};

const makeGetBlogListingTemplateConnections = () => {
  const getParentConnections = makeGetParentConnections();
  return createSelector([getParentConnections], parentConnections => {
    return getConnectionsForTemplateTypes(parentConnections, [TemplateTypes.blog.id, TemplateTypes.blog_listing.id]);
  });
};

const makeGetCodedFileConnections = () => {
  const getParentConnections = makeGetParentConnections();
  return createSelector([getParentConnections], parentConnections => {
    return getConnections(parentConnections, _node => {
      return _node.isCodedFile && /\.(css|js)$/.test(_node.model.path);
    });
  });
};

const makeGetEmailTemplateConnections = () => {
  const getParentConnections = makeGetParentConnections();
  return createSelector([getParentConnections], parentConnections => {
    const nonDragDropTypes = getConnectionsForTemplateTypes(parentConnections, [TemplateTypes.email_base_template.id, TemplateTypes.email.id, TemplateTypes.resubscribe_email.id, TemplateTypes.unsubscribe_confirmation_email.id, TemplateTypes.resubscribe_confirmation_email.id, TemplateTypes.optin_email.id, TemplateTypes.optin_followup_email.id]);
    return Object.assign({}, nonDragDropTypes, {}, getConnectionsForTemplateTypes(parentConnections, [TemplateTypes.drag_drop_email.id]));
  });
};

const makeGetSystemTemplateConnections = () => {
  const getParentConnections = makeGetParentConnections();
  return createSelector([getParentConnections], parentConnections => {
    return getConnectionsForTemplateTypes(parentConnections, [TemplateTypes.unmapped.id, TemplateTypes.error_page.id, TemplateTypes.subscription_preferences.id, TemplateTypes.unsubscribe_confirmation.id, TemplateTypes.unsubscribe_simple.id, TemplateTypes.password_prompt_page.id, ...Object.keys(systemMembershipTemplateTypes)]);
  });
};

const makeGetQuoteTemplateConnections = () => {
  const getParentConnections = makeGetParentConnections();
  return createSelector([getParentConnections], parentConnections => {
    return getConnectionsForTemplateTypes(parentConnections, [TemplateTypes.quote.id]);
  });
};

const makeGetSalesQuoteTemplateConnections = () => {
  const getParentConnections = makeGetParentConnections();
  return createSelector([getParentConnections], parentConnections => {
    return getConnections(parentConnections, _node => {
      return _node.isSalesQuoteTemplate;
    });
  });
};

const makeGetGlobalGroupConnections = () => {
  const getParentConnections = makeGetParentConnections();
  return createSelector([getParentConnections], parentConnections => {
    return getConnectionsForTemplateTypes(parentConnections, [TemplateTypes.global_group.id]);
  });
};

const makeGetPublishedPageConnections = () => {
  const getParentConnections = makeGetParentConnections();
  return createSelector([getParentConnections], parentConnections => {
    return getConnections(parentConnections, _node => {
      return _node.isPublishedPage;
    });
  });
};

const makeGetDraftPageConnections = () => {
  const getParentConnections = makeGetParentConnections();
  return createSelector([getParentConnections], parentConnections => {
    return getConnections(parentConnections, _node => {
      return _node.isDraftPage;
    });
  });
};

const makeGetSentEmailConnections = () => {
  const getParentConnections = makeGetParentConnections();
  return createSelector([getParentConnections], parentConnections => {
    return getConnections(parentConnections, _node => {
      return _node.isSentEmail;
    });
  });
};

const makeGetDraftEmailConnections = () => {
  const getParentConnections = makeGetParentConnections();
  return createSelector([getParentConnections], parentConnections => {
    return getConnections(parentConnections, _node => {
      return _node.isDraftEmail;
    });
  });
};

const makeGetCustomModuleConnections = () => {
  const getParentConnections = makeGetParentConnections();
  return createSelector([getParentConnections], parentConnections => {
    return getConnections(parentConnections, _node => {
      return _node.isCustomWidget;
    });
  });
};

const makeGetContentSettingsConnections = () => {
  const getParentConnections = makeGetParentConnections();
  return createSelector([getParentConnections], parentConnections => {
    return getConnections(parentConnections, _node => {
      return _node.isSiteSettings || _node.isDomainSettings;
    });
  });
};

const makeGetBlogConnections = () => {
  const getParentConnections = makeGetParentConnections();
  return createSelector([getParentConnections], parentConnections => {
    return getConnections(parentConnections, _node => {
      return _node.isBlog;
    });
  });
};

const makeGetGlobalContentPartialConnections = () => {
  const getParentConnections = makeGetParentConnections();
  return createSelector([getParentConnections], parentConnections => {
    return getConnectionsForTemplateTypes(parentConnections, [TemplateTypes.global_content_partial.id]);
  });
};

export const makeGetParentConnectionsByCategory = () => {
  const getPageTemplateConnections = makeGetPageTemplateConnections();
  const getBlogPostTemplateConnections = makeGetBlogPostTemplateConnections();
  const getBlogListingTemplateConnections = makeGetBlogListingTemplateConnections();
  const getEmailTemplateConnections = makeGetEmailTemplateConnections();
  const getSystemTemplateConnections = makeGetSystemTemplateConnections();
  const getQuoteTemplateConnections = makeGetQuoteTemplateConnections();
  const getSalesQuoteTemplateConnections = makeGetSalesQuoteTemplateConnections();
  const getGlobalGroupConnections = makeGetGlobalGroupConnections();
  const getPublishedPageConnections = makeGetPublishedPageConnections();
  const getDraftPageConnections = makeGetDraftPageConnections();
  const getSentEmailConnections = makeGetSentEmailConnections();
  const getDraftEmailConnections = makeGetDraftEmailConnections();
  const getBlogConnections = makeGetBlogConnections();
  const getCodedFileConnections = makeGetCodedFileConnections();
  const getCustomModuleConnections = makeGetCustomModuleConnections();
  const getContentSettingsConnections = makeGetContentSettingsConnections();
  const getGlobalContentPartialConnections = makeGetGlobalContentPartialConnections();
  return createSelector([getPageTemplateConnections, getBlogPostTemplateConnections, getBlogListingTemplateConnections, getEmailTemplateConnections, getSystemTemplateConnections, getQuoteTemplateConnections, getSalesQuoteTemplateConnections, getGlobalGroupConnections, getPublishedPageConnections, getDraftPageConnections, getSentEmailConnections, getDraftEmailConnections, getBlogConnections, getCodedFileConnections, getCustomModuleConnections, getContentSettingsConnections, getGlobalContentPartialConnections], (pageTemplateConnections, blogPostTemplateConnections, blogListingTemplateConnections, emailTemplateConnections, systemTemplateConnections, quoteTemplateConnections, salesQuoteTemplateConnections, globalGroupConnections, publishedPageConnections, draftPageConnections, sentEmailConnections, draftEmailConnections, blogConnections, codedFileConnections, customModuleConnections, contentSettingsConnections, globalContentPartialConnnections) => {
    return SeamlessImmutable({
      [PUBLISHED_PAGE]: publishedPageConnections,
      [DRAFT_PAGE]: draftPageConnections,
      [SENT_EMAIL]: sentEmailConnections,
      [DRAFT_EMAIL]: draftEmailConnections,
      [BLOG]: blogConnections,
      [BLOG_POST_TEMPLATE]: blogPostTemplateConnections,
      [BLOG_LISTING_TEMPLATE]: blogListingTemplateConnections,
      [CODED_FILE]: codedFileConnections,
      [CUSTOM_WIDGET]: customModuleConnections,
      [EMAIL_TEMPLATE]: emailTemplateConnections,
      [PAGE_TEMPLATE]: pageTemplateConnections,
      [SYSTEM_TEMPLATE]: systemTemplateConnections,
      [QUOTE_TEMPLATE]: quoteTemplateConnections,
      [GLOBAL_GROUP]: globalGroupConnections,
      [CONTENT_SETTINGS]: contentSettingsConnections,
      [GLOBAL_CONTENT_PARTIAL]: globalContentPartialConnnections,
      [SALES_QUOTE_TEMPLATE]: salesQuoteTemplateConnections
    });
  });
};
export const makeGetParentConnectionsCount = () => {
  const getParentConnections = makeGetParentConnections();
  return createSelector([getParentConnections], parentConnections => {
    return Object.keys(getConnections(parentConnections, _node => {
      return !_node.isGlobalContent;
    })).length;
  });
};
export const makeGetLiveParentConnectionsCount = () => {
  const getParentConnectionsByCategory = makeGetParentConnectionsByCategory();
  return createSelector([getParentConnectionsByCategory], parentConnectionsByCategory => {
    const liveConnectionsByCategory = pick(parentConnectionsByCategory, CosIndexLiveConnectionCategories);
    return Object.keys(liveConnectionsByCategory).reduce((total, category) => {
      const deps = liveConnectionsByCategory[category];
      return total + Object.keys(deps).length;
    }, 0);
  });
};
export const makeGetFetchStatus = () => createSelector([getFetchStatusByAssetId], fetchStatus => fetchStatus || RequestStatus.UNINITIALIZED);
export const makeGetFetchError = () => createSelector([getFetchErrorByAssetId], fetchError => fetchError || null);

const getFolderConnectionsByAssetId = (state, props) => {
  const folderConnectionsAssetIds = getFolderConnectionsAssetIds(state, props) || [];
  return folderConnectionsAssetIds.reduce((connections, assetId) => {
    const getParentConnectionsByCategory = makeGetParentConnectionsByCategory();
    const parentConnectionsByCategory = getParentConnectionsByCategory(state, {
      assetId
    });
    return connections.merge({
      [assetId]: parentConnectionsByCategory
    });
  }, SeamlessImmutable({}));
};

export const makeGetFolderConnectionsByAssetId = () => createSelector([getFolderConnectionsByAssetId], folderConnectionsByAssetId => folderConnectionsByAssetId);
/**
 * Bulk COS index connections selectors
 */

export const getBulkFetchStatus = (state, props) => {
  const {
    assetIds
  } = props;
  const fetchStatuses = mapObject(RequestStatus, () => []);
  assetIds.forEach(assetId => {
    const fetchStatus = getFetchStatusByAssetId(state, {
      assetId
    });

    if (fetchStatus) {
      fetchStatuses[fetchStatus].push(assetId);
    }
  });

  if (fetchStatuses[RequestStatus.PENDING].length) {
    return RequestStatus.PENDING;
  } else if (fetchStatuses[RequestStatus.FAILED].length) {
    return RequestStatus.FAILED;
  } else if (fetchStatuses[RequestStatus.SUCCEEDED].length) {
    return RequestStatus.SUCCEEDED;
  }

  return RequestStatus.UNINITIALIZED;
};